import { PlusOutlined } from "@ant-design/icons"
import { Button, Empty, Flex, Input, List, Tag, Tooltip, Typography } from "antd"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { neutral } from "../../../../components/widgets/tokens.ts"
import type {
  CatalogWidgetSpec,
  WidgetCatalog
} from "../../../DashboardPage/redux/widgets.types.ts"
import {
  type LibraryGroupKey,
  groupCatalog
} from "../../helpers/reportCatalog.ts"

const CATEGORY_KEYS: ("all" | LibraryGroupKey)[] = [
  "all",
  "kpi",
  "chart",
  "table",
  "list",
  "other"
]

const { Text } = Typography

interface ReportAddWidgetPanelProps {
  catalog: WidgetCatalog | null
  loading?: boolean
  onAdd: (spec: CatalogWidgetSpec) => void
  addedSpecKeys?: ReadonlySet<string>
}

const EMPTY_SPEC_KEYS: ReadonlySet<string> = new Set()

export const ReportAddWidgetPanel = ({
  catalog,
  loading = false,
  onAdd,
  addedSpecKeys = EMPTY_SPEC_KEYS
}: ReportAddWidgetPanelProps) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState("")
  const [category, setCategory] = useState<"all" | LibraryGroupKey>("all")

  const groups = useMemo(() => {
    const all = groupCatalog(catalog, search)
    return category === "all"
      ? all
      : all.filter((group) => group.key === category)
  }, [catalog, search, category])

  return (
    <Flex
      vertical
      gap="middle"
      style={{
        width: 288,
        flex: "none",
        borderRight: `1px solid ${neutral.border}`,
        paddingRight: 16,
        overflow: "auto"
      }}
    >
      <Text strong>{t("reports.build.addWidget")}</Text>

      <Input.Search
        placeholder={t("reports.library.searchPlaceholder")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
      />

      <Flex wrap="wrap" gap={6}>
        {CATEGORY_KEYS.map((key) => (
          <Tag.CheckableTag
            key={key}
            checked={category === key}
            onChange={() => setCategory(key)}
          >
            {t(`reports.build.categories.${key}`)}
          </Tag.CheckableTag>
        ))}
      </Flex>

      {!loading && groups.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("reports.library.empty")}
        />
      )}

      {groups.map((group) => (
        <Flex vertical gap="small" key={group.key}>
          <Text
            type="secondary"
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.05em",
              textTransform: "uppercase"
            }}
          >
            {t(`reports.library.groups.${group.key}`)}
          </Text>
          <List
            size="small"
            loading={loading}
            dataSource={group.specs}
            renderItem={(spec) => {
              const added = addedSpecKeys.has(spec.key)
              return (
                <List.Item
                  key={spec.key}
                  actions={[
                    <Tooltip
                      key="add"
                      title={added ? t("reports.library.alreadyAdded") : ""}
                    >
                      <Button
                        type="text"
                        size="small"
                        icon={<PlusOutlined />}
                        aria-label={spec.title}
                        disabled={added}
                        onClick={() => onAdd(spec)}
                      />
                    </Tooltip>
                  ]}
                >
                  <List.Item.Meta
                    title={
                      added ? (
                        <Text type="secondary">{spec.title}</Text>
                      ) : (
                        spec.title
                      )
                    }
                  />
                </List.Item>
              )
            }}
          />
        </Flex>
      ))}

      <Tooltip title={t("reports.build.customWidgetSoon")}>
        <Button block icon={<PlusOutlined />} disabled>
          {t("reports.build.customWidget")}
        </Button>
      </Tooltip>
    </Flex>
  )
}
